import { Form, useLocation } from 'react-router'
import { Eyebrow, Heading } from '#app/components/brand/section.tsx'
import { Button } from '#app/components/ui/button.js'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/about.ts'
import { Footer } from './components/footer'
import Header from './components/header'

export const meta: Route.MetaFunction = () =>
	buildMeta({
		title: 'About | Competitor Backlinks',
		description:
			'Why we’re building a calmer, honest way to understand competitor backlinks. Free tool first, no dark patterns, built in the open.',
		path: '/about',
		image: '/img/og/about.png',
	})

const timeline = [
	{
		date: 'Background',
		title: 'Two decades operating',
		text: 'Ran businesses across personal injury law, local trades, and oil & gas manufacturing. Real operations, real numbers, not theory.',
	},
	{
		date: 'DFW AI',
		title: 'Systems built for operators',
		text: 'Founded DFW AI after watching capable operators lose to AI products built for nobody in particular. The systems here are how I run my own companies.',
	},
	{
		date: 'This site',
		title: 'The free checker and guides',
		text: 'Competitor Backlinks is one of those systems: a free tool that is useful before it asks for anything, and guides that define their terms.',
	},
	{
		date: 'The course',
		title: 'Packaging the process',
		text: 'The exact backlink research process, taught without the jargon with AI doing the heavy lifting. Enrolment opens soon.',
	},
	{
		date: 'Next',
		title: 'Monitoring, on autopilot',
		text: 'Turning the tool into something that watches your competitors links for you, so a lost or gained link never slips past.',
	},
]

const beliefs = [
	{
		n: '01',
		title: 'Clarity over data',
		text: 'More numbers isn’t more useful. We compete on how clear we make things, not how much we dump on you.',
	},
	{
		n: '02',
		title: 'Honest by default',
		text: 'We say when free is enough and when a tool isn’t worth it. That honesty is the whole business.',
	},
	{
		n: '03',
		title: 'Generous first',
		text: 'Give real value before asking for anything. The email and the sale come after the help.',
	},
	{
		n: '04',
		title: 'No dark patterns',
		text: 'No fake urgency, no manipulation. If we can’t win your trust fairly, we don’t want the click.',
	},
]

export default function About() {
	const location = useLocation()
	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />

			<main>
				{/* HERO */}
				<section className="mx-auto max-w-[900px] px-7 pt-15 pb-11">
					<Eyebrow className="mb-5">About · A DFW AI project</Eyebrow>
					<Heading
						as="h1"
						className="text-[clamp(36px,5.4vw,58px)] leading-[1.04] tracking-[-0.03em]"
					>
						I’m Brad. This is one of the systems I actually run.
					</Heading>
					<p className="mt-5 max-w-[640px] text-xl leading-relaxed text-pretty text-[#57565E]">
						Understanding your competitors’ backlinks shouldn’t require an
						enterprise contract or an SEO degree. Competitor Backlinks is a DFW
						AI project built on that belief, with an honest free tool and guides
						that define their terms.
					</p>
				</section>

				{/* STORY */}
				<section className="[&_strong]:text-ink mx-auto max-w-[680px] px-7 pt-14 text-[18.5px] leading-[1.75] text-[#33323A] [&_p]:mb-[22px] [&_strong]:font-semibold">
					<p>
						I’ve spent two decades building and running businesses across
						personal injury law, local trades, and oil &amp; gas manufacturing.
						I started DFW AI because I kept watching capable operators lose to
						AI products built for nobody in particular.
					</p>
					<p>
						The data was never the problem.{' '}
						<strong>The translation was.</strong> Tools like Ahrefs and Semrush
						are extraordinary, but they’re built for full-time SEOs at agencies,
						priced for teams, and designed like aircraft cockpits. If you’re an
						operator or a solo marketer, you’re paying enterprise prices to feel
						stupid.
					</p>
					<p>
						So Competitor Backlinks got one rule:{' '}
						<strong>
							every screen has to make a slightly-overwhelmed operator feel
							clearer, not dumber.
						</strong>{' '}
						A free tool that’s genuinely useful before it asks for anything.
						Guides that define their terms. Honest recommendations, including
						“you don’t need to pay for this yet.”
					</p>
					<p>
						These systems aren’t theory. They’re how I run my own companies, and
						what I install in others. If that sounds like your kind of company
						to keep, here’s how we got here.
					</p>
				</section>

				{/* TIMELINE */}
				<section className="mx-auto max-w-[760px] px-7 pt-[70px]">
					<Heading className="mb-8 text-[28px]">How we got here</Heading>
					<div className="flex flex-col">
						{timeline.map((t) => (
							<div
								key={t.date}
								className="grid grid-cols-[90px_1fr] gap-6 pb-7 sm:grid-cols-[110px_1fr]"
							>
								<div className="text-brand pt-px font-mono text-sm font-semibold">
									{t.date}
								</div>
								<div className="relative border-l-2 border-[#EAE5DC] pb-1 pl-6">
									<span className="bg-brand absolute top-1 -left-[7px] size-3 rounded-full border-2 border-[#FAF8F4]" />
									<div className="font-display mb-1.5 text-[17px] font-semibold">
										{t.title}
									</div>
									<p className="text-[15px] leading-relaxed text-[#57565E]">
										{t.text}
									</p>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* PRINCIPLES */}
				<section className="mx-auto max-w-[960px] px-7 pt-15">
					<Heading className="mb-7 text-center text-[28px]">
						What I believe
					</Heading>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{beliefs.map((b) => (
							<div
								key={b.n}
								className="border-hairline rounded-2xl border bg-white p-6"
							>
								<div className="text-brand mb-3 font-mono text-[22px] font-semibold">
									{b.n}
								</div>
								<div className="font-display mb-2 text-[17px] font-semibold">
									{b.title}
								</div>
								<p className="text-[14.5px] leading-relaxed text-[#57565E]">
									{b.text}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* AFFILIATE DISCLOSURE */}
				<section
					id="affiliate-disclosure"
					className="mx-auto max-w-[760px] scroll-mt-24 px-7 pt-16"
				>
					<div className="border-hairline rounded-[20px] border bg-white p-8">
						<Eyebrow className="mb-3">Affiliate disclosure</Eyebrow>
						<Heading className="mb-4 text-[22px]">
							How we make money, plainly
						</Heading>
						<div className="flex flex-col gap-3 text-[15.5px] leading-relaxed text-[#57565E]">
							<p>
								Some links on this site are affiliate links. If you buy a tool
								through one, we may earn a commission at no extra cost to you.
							</p>
							<p>
								It never changes what we recommend. We only point to tools we’d
								actually use, we say when a free approach is enough, and every
								affiliate link is disclosed where it appears. If a
								recommendation and a commission ever conflict, the
								recommendation wins.
							</p>
						</div>
					</div>
				</section>

				{/* FOLLOW ALONG */}
				<section className="mx-auto max-w-[760px] px-7 pt-[70px] pb-[90px]">
					<div className="border-brand bg-brand-wash rounded-[20px] border-[1.5px] p-9 text-center">
						<Heading className="mb-2.5 text-[26px]">Follow along</Heading>
						<p className="mx-auto mb-6 max-w-[460px] text-[16.5px] leading-relaxed text-[#4A4952]">
							One honest email a week: a guide, a lesson learned, and the
							current numbers. No funnel, unsubscribe anytime.
						</p>
						<Form
							method="post"
							action="/resources/subscribe"
							className="mx-auto flex max-w-[420px] flex-col gap-2.5 sm:flex-row"
						>
							<input
								type="hidden"
								name="redirectTo"
								value={location.pathname}
							/>
							<label htmlFor="about-email" className="sr-only">
								Email address for the weekly newsletter
							</label>
							<input
								id="about-email"
								name="email"
								type="email"
								placeholder="you@company.com"
								className="focus-visible:ring-brand text-ink h-12 flex-1 rounded-[11px] border-[1.5px] border-[#DAD5E8] bg-white px-4 font-mono text-[14.5px] outline-none focus-visible:ring-2"
							/>
							<Button type="submit" size="lg" className="h-12 rounded-[11px]">
								Subscribe
							</Button>
						</Form>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	)
}
